import { parseSessionKeyPath, type SessionKeyPath } from "../chat/session-key-path.ts";
import type { UiSettings } from "../storage.ts";
import type { SessionsUsageEntry, SessionsUsageResult, SessionsUsageTotals } from "../types.ts";
import { sessionKeyMatchesScopePath } from "./sessions-filter.ts";

export type LlmStatsTabId =
  | "connect"
  | "overview"
  | "analytics"
  | "fleet"
  | "sessions"
  | "trace"
  | "local";

export function resolveLlmStatsScopePath(settings: UiSettings): SessionKeyPath {
  if (settings.llmStatsScopeLinked) {
    return parseSessionKeyPath(settings.sessionKey);
  }
  return settings.llmStatsScopePath;
}

function emptyTotals(): SessionsUsageTotals {
  return {
    input: 0,
    output: 0,
    cacheRead: 0,
    cacheWrite: 0,
    totalTokens: 0,
    totalCost: 0,
    inputCost: 0,
    outputCost: 0,
    cacheReadCost: 0,
    cacheWriteCost: 0,
    missingCostEntries: 0,
  };
}

function addTotals(
  acc: SessionsUsageTotals,
  usage: SessionsUsageEntry["usage"],
): SessionsUsageTotals {
  if (!usage) {
    return acc;
  }
  return {
    input: acc.input + (usage.input ?? 0),
    output: acc.output + (usage.output ?? 0),
    cacheRead: acc.cacheRead + (usage.cacheRead ?? 0),
    cacheWrite: acc.cacheWrite + (usage.cacheWrite ?? 0),
    totalTokens: acc.totalTokens + (usage.totalTokens ?? 0),
    totalCost: acc.totalCost + (usage.totalCost ?? 0),
    inputCost: acc.inputCost + (usage.inputCost ?? 0),
    outputCost: acc.outputCost + (usage.outputCost ?? 0),
    cacheReadCost: acc.cacheReadCost + (usage.cacheReadCost ?? 0),
    cacheWriteCost: acc.cacheWriteCost + (usage.cacheWriteCost ?? 0),
    missingCostEntries: acc.missingCostEntries + (usage.missingCostEntries ?? 0),
  };
}

function sumSessionTotals(sessions: SessionsUsageEntry[]): SessionsUsageTotals {
  return sessions.reduce((acc, session) => addTotals(acc, session.usage), emptyTotals());
}

function recomputeAggregates(sessions: SessionsUsageEntry[]): SessionsUsageResult["aggregates"] {
  const messages = { total: 0, user: 0, assistant: 0, toolCalls: 0, toolResults: 0, errors: 0 };
  const toolMap = new Map<string, number>();
  const modelMap = new Map<
    string,
    { provider?: string; model?: string; count: number; totals: SessionsUsageTotals }
  >();
  const agentMap = new Map<string, SessionsUsageTotals>();

  for (const session of sessions) {
    const usage = session.usage;
    if (!usage) {
      continue;
    }
    const mc = usage.messageCounts;
    if (mc) {
      messages.total += mc.total ?? 0;
      messages.user += mc.user ?? 0;
      messages.assistant += mc.assistant ?? 0;
      messages.toolCalls += mc.toolCalls ?? 0;
      messages.toolResults += mc.toolResults ?? 0;
      messages.errors += mc.errors ?? 0;
    }
    const tu = usage.toolUsage;
    if (tu?.tools) {
      for (const t of tu.tools) {
        toolMap.set(t.name, (toolMap.get(t.name) ?? 0) + (t.count ?? 0));
      }
    }
    const provider = session.modelProvider ?? session.providerOverride ?? "unknown";
    const model = session.model ?? session.modelOverride ?? "unknown";
    const modelKey = `${provider}/${model}`;
    const existingModel = modelMap.get(modelKey);
    if (existingModel) {
      existingModel.count += 1;
      existingModel.totals = addTotals(existingModel.totals, usage);
    } else {
      modelMap.set(modelKey, {
        provider,
        model,
        count: 1,
        totals: addTotals(emptyTotals(), usage),
      });
    }
    const agentId = session.agentId ?? parseSessionKeyPath(session.key)[1] ?? "unknown";
    agentMap.set(agentId, addTotals(agentMap.get(agentId) ?? emptyTotals(), usage));
  }

  const tools = [...toolMap.entries()].map(([name, count]) => ({ name, count }));
  return {
    messages,
    tools: {
      totalCalls: tools.reduce((n, t) => n + t.count, 0),
      uniqueTools: tools.length,
      tools,
    },
    byModel: [...modelMap.values()],
    byProvider: [...modelMap.values()],
    byAgent: [...agentMap.entries()].map(([agentId, totals]) => ({ agentId, totals })),
    byChannel: [],
    daily: [],
  };
}

/** Narrow session usage + totals to the active scope path (agent → kind → id). */
export function filterUsageResultByScope(
  result: SessionsUsageResult | null,
  settings: UiSettings,
): SessionsUsageResult | null {
  if (!result) {
    return null;
  }
  if (settings.llmStatsViewAll) {
    return result;
  }
  const path = resolveLlmStatsScopePath(settings);
  if (!path.some((segment) => segment.trim().length > 0)) {
    return result;
  }
  const sessions = result.sessions.filter((session) =>
    sessionKeyMatchesScopePath(session.key, path),
  );
  return {
    ...result,
    sessions,
    totals: sumSessionTotals(sessions),
    aggregates: recomputeAggregates(sessions),
  };
}

export function countUsageSessionsInScope(
  result: SessionsUsageResult | null,
  settings: UiSettings,
): { scoped: number; total: number } {
  const total = result?.sessions.length ?? 0;
  if (!result || settings.llmStatsViewAll) {
    return { scoped: total, total };
  }
  const path = resolveLlmStatsScopePath(settings);
  if (!path.some((segment) => segment.trim().length > 0)) {
    return { scoped: total, total };
  }
  const scoped = result.sessions.filter((session) =>
    sessionKeyMatchesScopePath(session.key, path),
  ).length;
  return { scoped, total };
}
